<template>
  <transition name="modal-fade">
    <div v-if="modelValue" class="modal-backdrop" @click.self="handleBackdropClick">
      <div class="modal-container">
        <div class="modal-header">
          <i class="fas fa-clock modal-icon"></i>
          <h2 class="modal-title">{{ t('inactivity.title') }}</h2>
        </div>
        
        <div class="modal-body">
          <template v-if="!sessionAlreadyExpired">
            <p class="modal-message">{{ t('inactivity.message', { minutes: inactivityMinutes }) }}</p>

            <div class="countdown-container">
              <div class="countdown-circle">
                <svg class="countdown-svg" :style="{ '--progress': progressPercentage }">
                  <circle class="countdown-bg" cx="60" cy="60" r="54"></circle>
                  <circle class="countdown-progress" cx="60" cy="60" r="54"></circle>
                </svg>
                <div class="countdown-text">
                  <span class="countdown-number">{{ countdown }}</span>
                  <span class="countdown-label">{{ t('inactivity.seconds') }}</span>
                </div>
              </div>
            </div>

            <p class="modal-warning">{{ t('inactivity.warning') }}</p>
          </template>
        </div>

        <div v-if="!sessionAlreadyExpired" class="modal-footer">
          <button
            class="btn btn-secondary"
            @click="handleLogout"
            :disabled="isProcessing"
          >
            <i class="fas fa-sign-out-alt"></i>
            {{ t('inactivity.logout') }}
          </button>
          <button
            class="btn btn-primary"
            @click="handleContinue"
            :disabled="isProcessing"
          >
            <i class="fas fa-check-circle"></i>
            {{ t('inactivity.continue') }}
          </button>
        </div>

        <!--
          El overlay de "procesando" es opaco y cubre todo el modal-container,
          incluido el modal-body — si sessionAlreadyExpired muestra su texto
          ahí, el overlay lo tapa por completo apenas isProcessing se activa
          (ambos quedan en true en el mismo tick). El mensaje de cierre vive
          DENTRO del overlay para quedar siempre legible junto al spinner.
        -->
        <div v-if="isProcessing" class="modal-overlay">
          <p v-if="sessionAlreadyExpired" class="overlay-message">{{ t('inactivity.session-closing') }}</p>
          <div class="spinner"></div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import sweetalertUtil from '@/utils/sweetalertUtil'

// Misma clave que useInactivityDetector.js: registro persistido de la última
// actividad real del usuario, para calcular el deadline contra el reloj real
// en vez de contra el momento en que este modal llega a abrirse (que puede
// estar retrasado por throttling del navegador en tabs en background).
const ACTIVITY_STORAGE_KEY = '__last_activity_at'

export default {
  name: 'InactivityModal',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    countdownTime: {
      type: Number,
      default: 60
    },
    inactivityMinutes: {
      type: Number,
      default: 55
    }
  },
  emits: ['update:modelValue', 'continue', 'logout', 'timeout'],
  setup(props, { emit }) {
    const store = useStore()
    const router = useRouter()
    const { t } = useI18n()
    
    const countdown = ref(props.countdownTime)
    const deadline = ref(0)
    const isProcessing = ref(false)
    // Cuando el tiempo total ya se cumplió antes de que hubiera oportunidad
    // de continuar (al abrir el modal o al volver de una pestaña en
    // background), se oculta el countdown/botones y se muestra un mensaje
    // dedicado — evita superponer el texto de countdown, el overlay de
    // "procesando" y la alerta de logout, todo a la vez, ilegible.
    const sessionAlreadyExpired = ref(false)
    let countdownInterval = null
    
    // Progreso visual del círculo (0 a 1)
    const progressPercentage = computed(() => {
      return countdown.value / props.countdownTime
    })
    
    /**
     * Inicia el countdown
     */
    const startCountdown = () => {
      sessionAlreadyExpired.value = false

      const lastActivityAt = Number(sessionStorage.getItem(ACTIVITY_STORAGE_KEY)) || Date.now()
      const inactivityMs = props.inactivityMinutes * 60 * 1000
      deadline.value = lastActivityAt + inactivityMs + props.countdownTime * 1000

      const remainingMs = deadline.value - Date.now()
      countdown.value = remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0

      if (countdown.value <= 0) {
        sessionAlreadyExpired.value = true
        // El tiempo total (inactividad + countdown) ya se cumplió antes de
        // que el modal llegara a mostrarse (p.ej. la pestaña estuvo en
        // background) — no dar oportunidad de continuar, forzar el cierre
        // de sesión directo, igual que si el countdown hubiera llegado a 0.
        handleTimeout()
        return
      }

      countdownInterval = setInterval(() => {
        countdown.value--

        if (countdown.value <= 0) {
          stopCountdown()
          handleTimeout()
        }
      }, 1000)
    }
    
    /**
     * Detiene el countdown
     */
    const stopCountdown = () => {
      if (countdownInterval) {
        clearInterval(countdownInterval)
        countdownInterval = null
      }
    }
    
    /**
     * Maneja el timeout (cuando llega a 0)
     */
    const handleTimeout = async () => {
      emit('timeout')
      await performLogout(t('inactivity.session-closed-by-timeout'))
    }
    
    /**
     * Maneja el click en "Continuar sesión"
     */
    const handleContinue = async () => {
      // Guard de reentrancia: ignorar clicks mientras ya hay un logout/refresh en curso
      if (isProcessing.value) return

      stopCountdown()

      // Validar contra el reloj real, no contra el contador visual: si el
      // tiempo permitido ya venció (p.ej. la pestaña estuvo en background y
      // el setInterval se retrasó), no se reactiva la sesión.
      if (Date.now() >= deadline.value) {
        await performLogout(t('inactivity.session-closed-by-timeout'))
        return
      }

      isProcessing.value = true

      try {
        const { message, error } = await store.dispatch('auth/refreshToken')

        if (error) {
          await sweetalertUtil.fire({
            icon: 'warning',
            title: t('inactivity.session-expired-title'),
            html: message || t('inactivity.session-expired-message'),
            confirmButtonText: t('accept'),
            allowOutsideClick: false
          })
          await performLogout()
          return
        }

        emit('continue')
        emit('update:modelValue', false)

      } catch (err) {
        console.error('Error handling continue session:', err)
        await performLogout()
      } finally {
        isProcessing.value = false
      }
    }
    
    /**
     * Maneja el click en "Cerrar sesión"
     */
    const handleLogout = async () => {
      stopCountdown()
      emit('logout')
      await performLogout(t('inactivity.session-closed-by-user'))
    }
    
    /**
     * Ejecuta el logout manteniendo el modal visible (con overlay de carga)
     * hasta que el proceso termina, para que el usuario no tenga acceso a la plataforma.
     */
    const performLogout = async (message = null) => {
      isProcessing.value = true
      try {
        const { error } = await store.dispatch('auth/logout')
        if (error) {
          await store.dispatch('auth/clearAuthToken')
        }
        if (message) {
          await sweetalertUtil.fire({
            icon: 'info',
            title: t('inactivity.session-closed-title'),
            html: message,
            confirmButtonText: t('accept'),
            timer: 3000,
            timerProgressBar: true
          })
        }
      } catch (err) {
        console.error('Error during logout:', err)
        await store.dispatch('auth/clearAuthToken')
      } finally {
        isProcessing.value = false
        emit('update:modelValue', false)
      }
      router.push({ name: 'auth-login' })
    }
    
    /**
     * Prevenir cierre accidental del modal
     */
    const handleBackdropClick = () => {
      // No hacer nada, forzar al usuario a tomar una decisión
    }

    /**
     * El setInterval del countdown también está sujeto al throttling de
     * pestañas en background: si el modal ya estaba abierto y la pestaña
     * pasa a segundo plano, el countdown visible puede quedar "congelado"
     * (el botón "Continuar" se ve activo aunque el tiempo real ya se haya
     * agotado, y solo se rechaza al hacer click). Al volver a la pestaña,
     * re-sincronizar de inmediato contra el deadline real en vez de
     * esperar al siguiente tick del setInterval.
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (!props.modelValue) return
      if (isProcessing.value) return

      const remainingMs = deadline.value - Date.now()

      if (remainingMs <= 0) {
        sessionAlreadyExpired.value = true
        stopCountdown()
        handleTimeout()
        return
      }

      countdown.value = Math.ceil(remainingMs / 1000)
    }

    // Observar cuando se abre el modal para iniciar countdown
    watch(() => props.modelValue, (newValue) => {
      if (newValue) {
        startCountdown()
      } else {
        stopCountdown()
        sessionAlreadyExpired.value = false
      }
    })

    onMounted(() => {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    })

    // Limpiar al desmontar
    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopCountdown()
    })
    
    return {
      t,
      countdown,
      isProcessing,
      sessionAlreadyExpired,
      progressPercentage,
      handleContinue,
      handleLogout,
      handleBackdropClick
    }
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  width: 90%;
  padding: 32px;
  position: relative;
  animation: modalSlideIn 0.3s ease-out;
}

.modal-header {
  text-align: center;
  margin-bottom: 24px;
}

.modal-icon {
  font-size: 48px;
  color: #0286C1;
  margin-bottom: 16px;
  animation: pulse 2s infinite;
}

.modal-title {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.modal-body {
  text-align: center;
  margin-bottom: 24px;
}

.modal-message {
  font-size: 16px;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 24px;
}

.countdown-container {
  display: flex;
  justify-content: center;
  margin: 32px 0;
}

.countdown-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.countdown-svg {
  width: 120px;
  height: 120px;
  transform: rotate(-90deg);
}

.countdown-bg {
  fill: none;
  stroke: #e5e7eb;
  stroke-width: 8;
}

.countdown-progress {
  fill: none;
  stroke: #0286C1;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 339.292;
  stroke-dashoffset: calc(339.292 * (1 - var(--progress)));
  transition: stroke-dashoffset 1s linear;
}

.countdown-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.countdown-number {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.countdown-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  margin-top: 4px;
}

.modal-warning {
  font-size: 14px;
  color: #dc2626;
  font-weight: 500;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background: #0286C1;
  color: white;
}

.btn-primary:not(:disabled):hover {
  background: #0279b1;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(2, 134, 193, 0.3);
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-secondary:not(:disabled):hover {
  background: #4b5563;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 24px;
  border-radius: 16px;
}

.overlay-message {
  font-size: 16px;
  color: #1f2937;
  font-weight: 500;
  text-align: center;
  line-height: 1.5;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Animaciones */
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 640px) {
  .modal-container {
    padding: 24px;
  }
  
  .modal-title {
    font-size: 20px;
  }
  
  .modal-footer {
    flex-direction: column-reverse;
  }
  
  .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>

